import React, { useState, useId, useContext, createContext, useCallback, useRef } from 'react';
import './Accordion.css';

export interface AccordionItem {
  /** Unique identifier for the accordion item */
  id: string;
  /** Accordion header/trigger label */
  label: React.ReactNode;
  /** Accordion panel content */
  content: React.ReactNode;
  /** Whether the item is disabled */
  disabled?: boolean;
}

export interface AccordionProps {
  /** Array of accordion item definitions */
  items: AccordionItem[];
  /** Controlled: currently expanded item IDs */
  expandedItems?: string[];
  /** Uncontrolled: initial expanded item IDs */
  defaultExpandedItems?: string[];
  /** Allow multiple items open simultaneously (default: false) */
  allowMultiple?: boolean;
  /** Called when expanded items change */
  onExpandedItemsChange?: (itemIds: string[]) => void;
  /** Custom class for container */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

export interface AccordionGroupProps {
  /** Accordion components to coordinate */
  children: React.ReactNode;
  /** Custom class for container */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

// Context for AccordionGroup coordination
interface AccordionGroupContextValue {
  activeAccordionId: string | null;
  activeItemId: string | null;
  setActive: (accordionId: string, itemId: string | null) => void;
}

const AccordionGroupContext = createContext<AccordionGroupContextValue | null>(null);

export const AccordionGroup: React.FC<AccordionGroupProps> = ({
  children,
  className = '',
  style,
}) => {
  const [activeAccordionId, setActiveAccordionId] = useState<string | null>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const setActive = useCallback((accordionId: string, itemId: string | null) => {
    setActiveAccordionId(accordionId);
    setActiveItemId(itemId);
  }, []);

  return (
    <AccordionGroupContext.Provider value={{ activeAccordionId, activeItemId, setActive }}>
      <div
        className={`hi-ui-accordion-group ${className}`.trim()}
        style={style}
        data-testid="accordion-group"
      >
        {children}
      </div>
    </AccordionGroupContext.Provider>
  );
};

export const Accordion: React.FC<AccordionProps> = ({
  items,
  expandedItems,
  defaultExpandedItems = [],
  allowMultiple = false,
  onExpandedItemsChange,
  className = '',
  style,
}) => {
  const generatedId = useId();
  const accordionId = `hi-ui-accordion-${generatedId}`;
  const groupContext = useContext(AccordionGroupContext);
  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Internal state for uncontrolled mode
  const [internalExpandedItems, setInternalExpandedItems] =
    useState<string[]>(defaultExpandedItems);

  // Determine if controlled
  const isControlled = expandedItems !== undefined;

  // Get current expanded items based on context or local state
  const getCurrentExpandedItems = (): string[] => {
    if (groupContext) {
      // In a group: only this accordion's active item is expanded
      if (groupContext.activeAccordionId === accordionId && groupContext.activeItemId) {
        return [groupContext.activeItemId];
      }
      return [];
    }
    return isControlled ? expandedItems : internalExpandedItems;
  };

  const currentExpandedItems = getCurrentExpandedItems();

  const isExpanded = (itemId: string): boolean => {
    return currentExpandedItems.includes(itemId);
  };

  const handleToggle = (itemId: string) => {
    let newExpandedItems: string[];

    if (groupContext) {
      // In a group: toggle single item, notify group
      const isCurrentlyExpanded =
        groupContext.activeAccordionId === accordionId && groupContext.activeItemId === itemId;
      groupContext.setActive(accordionId, isCurrentlyExpanded ? null : itemId);
      newExpandedItems = isCurrentlyExpanded ? [] : [itemId];
    } else if (allowMultiple) {
      // Multiple mode: add/remove from array
      newExpandedItems = isExpanded(itemId)
        ? currentExpandedItems.filter((id) => id !== itemId)
        : [...currentExpandedItems, itemId];
    } else {
      // Single mode: toggle or replace
      newExpandedItems = isExpanded(itemId) ? [] : [itemId];
    }

    if (!isControlled && !groupContext) {
      setInternalExpandedItems(newExpandedItems);
    }
    onExpandedItemsChange?.(newExpandedItems);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const enabledIndices = items
      .map((item, i) => (!item.disabled ? i : -1))
      .filter((i) => i !== -1);
    const currentEnabledIndex = enabledIndices.indexOf(index);

    let targetIndex: number | null = null;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (currentEnabledIndex < enabledIndices.length - 1) {
          targetIndex = enabledIndices[currentEnabledIndex + 1];
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (currentEnabledIndex > 0) {
          targetIndex = enabledIndices[currentEnabledIndex - 1];
        }
        break;
      case 'Home':
        e.preventDefault();
        targetIndex = enabledIndices[0];
        break;
      case 'End':
        e.preventDefault();
        targetIndex = enabledIndices[enabledIndices.length - 1];
        break;
    }

    if (targetIndex !== null) {
      triggerRefs.current[targetIndex]?.focus();
    }
  };

  return (
    <div className={`hi-ui-accordion ${className}`.trim()} style={style} data-testid="accordion">
      {items.map((item, index) => {
        const triggerId = `${accordionId}-trigger-${item.id}`;
        const panelId = `${accordionId}-panel-${item.id}`;
        const expanded = isExpanded(item.id);

        return (
          <div
            key={item.id}
            className={`hi-ui-accordion__item ${expanded ? 'hi-ui-accordion__item--expanded' : ''} ${item.disabled ? 'hi-ui-accordion__item--disabled' : ''}`}
            data-testid={`accordion-item-${item.id}`}
          >
            <button
              ref={(el) => {
                triggerRefs.current[index] = el;
              }}
              id={triggerId}
              type="button"
              className="hi-ui-accordion__trigger"
              aria-expanded={expanded}
              aria-controls={panelId}
              aria-disabled={item.disabled || undefined}
              disabled={item.disabled}
              onClick={() => !item.disabled && handleToggle(item.id)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              data-testid={`accordion-trigger-${item.id}`}
            >
              <span className="hi-ui-accordion__label">{item.label}</span>
              <svg
                className="hi-ui-accordion__chevron"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div
              id={panelId}
              role="region"
              aria-labelledby={triggerId}
              className="hi-ui-accordion__panel"
              data-testid={`accordion-panel-${item.id}`}
            >
              <div className="hi-ui-accordion__content">{item.content}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
